import { defineStore } from 'pinia'
import type { User, Session, UserRole } from '@/types'
import { SESSION_TIMEOUT_MS, SESSION_REFRESH_THRESHOLD_MS } from '@/types'
import { apiClient, setSessionIdGetter } from '@/services/apiClient'

// API Base URL
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1'

// ============================================
// API User (from GET /users)
// ============================================

export interface ApiUser {
  id: string
  name: string
  email: string
  role: UserRole
}

// ============================================
// State Interface
// ============================================

interface AuthState {
  user: User | null
  session: Session | null
  users: ApiUser[]
  isLoading: boolean
  error: string | null
}

// ============================================
// Store Definition
// ============================================

export const useAuthStore = defineStore('auth', {
  // ----------------------------------------
  // State
  // ----------------------------------------
  state: (): AuthState => ({
    user: null,
    session: null,
    users: [],
    isLoading: false,
    error: null
  }),

  // ----------------------------------------
  // Getters
  // ----------------------------------------
  getters: {
    /**
     * Check if user is authenticated with valid session
     */
    isAuthenticated(): boolean {
      return !!this.user && !!this.session && this.sessionValid
    },

    /**
     * Get current user role
     */
    userRole(): UserRole | null {
      return this.user?.role ?? null
    },

    /**
     * Check if user is admin
     */
    isAdmin(): boolean {
      return this.user?.role === 'admin'
    },

    /**
     * Check if user is standard or admin
     */
    isStandardOrAbove(): boolean {
      return this.user?.role === 'admin' || this.user?.role === 'standard'
    },

    /**
     * Check if session is still valid (not expired)
     */
    sessionValid(): boolean {
      if (!this.session) return false
      return this.session.expiresAt > Date.now()
    },

    /**
     * Check if session needs refresh (within 5 min of expiration)
     */
    sessionNeedsRefresh(): boolean {
      if (!this.session) return false
      const timeUntilExpiry = this.session.expiresAt - Date.now()
      return timeUntilExpiry > 0 && timeUntilExpiry < SESSION_REFRESH_THRESHOLD_MS
    },

    /**
     * Get time remaining in session (ms)
     */
    sessionTimeRemaining(): number {
      if (!this.session) return 0
      return Math.max(0, this.session.expiresAt - Date.now())
    }
  },

  // ----------------------------------------
  // Actions
  // ----------------------------------------
  actions: {
    /**
     * Fetch users list from API (public endpoint, no auth needed)
     */
    async fetchUsers(): Promise<void> {
      try {
        const response = await fetch(`${API_BASE_URL}/users`)
        const result = await response.json()
        if (response.ok && result.data) {
          this.users = result.data
        }
      } catch (e) {
        console.error('[AuthStore] Failed to fetch users:', e)
      }
    },

    /**
     * Login with user ID and PIN
     * @param userId - User ID
     * @param pin - User PIN (4 digits)
     * @returns true if login successful
     */
    async login(userId: string, pin: string): Promise<boolean> {
      this.isLoading = true
      this.error = null

      try {
        // Find user email from fetched users list
        const selectedUser = this.users.find(u => u.id === userId)

        if (!selectedUser) {
          this.error = 'Utilisateur non trouvé'
          return false
        }

        // Call backend API
        const response = await fetch(`${API_BASE_URL}/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: selectedUser.email, pin })
        })

        const result = await response.json()

        if (!response.ok || result.error) {
          this.error = result.message || 'Code PIN incorrect'
          return false
        }

        // Store session from backend
        const now = Date.now()
        this.session = {
          id: result.data.sessionId,
          userId: result.data.user.id,
          createdAt: now,
          expiresAt: now + SESSION_TIMEOUT_MS
        }

        // Store user from backend
        this.user = {
          id: result.data.user.id,
          email: result.data.user.email,
          name: result.data.user.name,
          role: result.data.user.role
        }

        // Register session getter for apiClient
        setSessionIdGetter(() => this.session?.id ?? null)

        console.log('[AuthStore] Login successful:', this.user.name)
        return true
      } catch (e) {
        const error = e as Error
        this.error = error.message || 'Erreur de connexion'
        console.error('[AuthStore] Login failed:', this.error)
        return false
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Logout and clear session
     */
    logout(): void {
      console.log('[AuthStore] Logging out:', this.user?.email)
      this.user = null
      this.session = null
      this.error = null
    },

    /**
     * Check if current session is valid
     * @returns true if session is valid
     */
    checkSession(): boolean {
      if (!this.session || !this.user) {
        return false
      }

      if (!this.sessionValid) {
        console.log('[AuthStore] Session expired, logging out')
        this.logout()
        return false
      }

      // Check if refresh is needed
      if (this.sessionNeedsRefresh) {
        console.log('[AuthStore] Session needs refresh')
        this.refreshSession()
      }

      return true
    },

    /**
     * Refresh session against the backend.
     * Backend returns absolute expiresAt; we sync local state with it.
     * On 401, the global apiClient handler will logout + redirect.
     */
    async refreshSession(): Promise<void> {
      if (!this.session) return

      try {
        const result = await apiClient.post<{ data: { expiresAt: number } }>('/auth/refresh', null)
        if (result?.data?.expiresAt) {
          this.session = {
            ...this.session,
            expiresAt: result.data.expiresAt
          }
          console.log('[AuthStore] Session refreshed')
        }
      } catch (e) {
        console.error('[AuthStore] Session refresh failed:', e)
        // 401 is handled by the global handler; other errors → let session expire naturally
      }
    },

    /**
     * Change a user's PIN
     * Admin: can change any user (no currentPin needed)
     * Non-admin: can only change own PIN (currentPin required)
     */
    async changePin(userId: string, newPin: string, currentPin?: string): Promise<boolean> {
      this.isLoading = true
      this.error = null

      try {
        await apiClient.put(`/users/${userId}/pin`, {
          newPin,
          ...(currentPin ? { currentPin } : {})
        })
        return true
      } catch (e) {
        const error = e as Error
        this.error = error.message || 'Erreur lors du changement de PIN'
        return false
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Clear error state
     */
    clearError(): void {
      this.error = null
    },

    /**
     * Set error manually (for use by other stores/components)
     */
    setError(message: string): void {
      this.error = message
    }
  },

  // ----------------------------------------
  // Persistence
  // ----------------------------------------
  persist: {
    key: 'vfr-auth',
    storage: localStorage,
    paths: ['user', 'session'], // Don't persist isLoading/error
    afterRestore: (ctx) => {
      // Re-register session getter after restore from localStorage
      if (ctx.store.session?.id) {
        setSessionIdGetter(() => ctx.store.session?.id ?? null)
        console.log('[AuthStore] Session restored from storage')
      }
    }
  }
})

export default useAuthStore
